import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../../hooks/store-hooks";
import { useUIService } from "../../../../../use-cases/ui";
import {
  useExtracoesService,
  useListaDeduplicacaoService,
} from "../../../../../use-cases/geo-veles";
import {
  Box,
  Button,
  debounce,
  Divider,
  Grid,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import {
  IExtracaoTableItem,
  IListaDeduplicacao,
  IRecorte,
  ITipoUsuario,
} from "../../../../../application/models";
import { format } from "date-fns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { SkeletonVelesTable } from "../../../../components";
import { DownloadCell } from "./DownloadCell";
import { Add, FilterAltOff } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import { RecortesProjetoList } from "../recortes-do-projeto/recortes-projeto-list";
import { RecorteCBOFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/cbo";
import { RecorteClasseSocialFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/classe-social";
import { RecorteContatoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/contato";
import { RecorteDataNascimentoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/data-nascimento";
import { RecorteEscolaridadeFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/escolaridade";
import { RecorteEstadoCivilFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/estado-civil";
import { RecorteIdadeFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/idade";
import { RecorteLocalizacaoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/localizacao";
import { RecorteProfissaoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/profissao";
import { RecorteRendaAnualFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/renda-anual";
import { RecorteRendaMensalFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/renda-mensal";
import { RecorteScoreFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/score";
import { RecorteSexoFilterPreview } from "../recortes-do-projeto/recortes-projeto-list/sexo";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { formatCPF, formatCep } from "../../../../../utils";
import { ListasCidadesFilterPreview } from "../detalhes-do-projeto/filtros/localizacao/lista-cidades/forms/listaCidadesFilterPreview";
import { ListasCboFilterPreview } from "../detalhes-do-projeto/filtros/cbo/forms/ListasCboFilterPreview";

export const ExtracoesList = () => {
  // State
  const {
    extracoes,
    extracoesTotal,
    extracaoListFiltros,
    extracoesPagination,
  } = useAppSelector((state) => state.extracoes);

  const [cpfSearchTerms, setCpfSearchTerms] = useState<{
    [key: string]: string;
  }>({});

  const [cepSearchTerms, setCepSearchTerms] = useState<{
    [key: string]: string;
  }>({});

  const { projetos } = useAppSelector((state) => state.projetos);
  // Use State
  const { currentUser } = useAppSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const formatCPFProgressivo = (cpf: string): string => {
    return cpf
      .replace(/\D/g, "")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  const formatCepProgressivo = (cep: string): string => {
    return cep.replace(/\D/g, "").replace(/(\d{5})(\d{1,3})$/, "$1-$2");
  };


  // Service
  const { setActivePage, changeTitleAndMetaDesc } = useUIService();
  const {
    setExtracoesPagination,
    getExtracoes,
    setExtracaoListFiltrosNome,
    resetExtracaoListFiltrosNome,
    setExtracaoListFiltrosInclusaoEnd,
    setExtracaoListFiltrosInclusaoStart,
    resetExtracaoListFiltrosInclusaoEnd,
    resetExtracaoListFiltrosInclusaoStart,
    setExtracaoListFiltrosCliente,
    resetExtracaoListFiltrosCliente,
    setExtracaoListFiltrosUsuario,
    resetExtracaoListFiltrosUsuario,
    resetExtracaoListFiltros,
    getExtracoesFindProjetos,
    getRecortes,
    setVefificarCpfsDuplicados,
  } = useExtracoesService();

  const { recortesExtracao } = useAppSelector((state) => state.recorteExtracao);
  const { getListasDeduplicacaoExtracao } = useListaDeduplicacaoService();
  const isFirstRender = useRef(false);

  const handleNomeChange = debounce((value: string) => {
    if (value.length > 1) {
      setExtracaoListFiltrosNome(value);
    } else {
      resetExtracaoListFiltrosNome();
    }
    setExtracoesPagination({
      ...extracoesPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleClienteChange = debounce((value: string) => {
    if (value.length > 1) {
      setExtracaoListFiltrosCliente(value);
    } else {
      resetExtracaoListFiltrosCliente();
    }
    setExtracoesPagination({
      ...extracoesPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleNomeUsuarioChange = debounce((value: string) => {
    if (value.length > 1) {
      setExtracaoListFiltrosUsuario(value);
    } else {
      resetExtracaoListFiltrosUsuario();
    }
    setExtracoesPagination({
      ...extracoesPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const handleUsuarioChange = debounce((value: string) => {
    if (value.length > 1) {
      setExtracaoListFiltrosUsuario(value);
    } else {
      resetExtracaoListFiltrosUsuario();
    }
    setExtracoesPagination({
      ...extracoesPagination,
      page: 0,
    });
  }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE));

  const areRecortesIguais = (a: IRecorte[] | null, b: IRecorte[] | null) => {
    if (!a || !b || a.length !== b.length) return false;

    const nomesA = a.map((r) => r.Nome).sort();
    const nomesB = b.map((r) => r.Nome).sort();

    return nomesA.every((nome, i) => nome === nomesB[i]);
  };

  const columns = useMemo<MRT_ColumnDef<IExtracaoTableItem>[]>(() => {
    const columnsToSet: MRT_ColumnDef<IExtracaoTableItem>[] = [
      {
        accessorKey: "extracao.Nome",
        header: "Nome Extração",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por nome"
            variant="outlined"
            size="small"
            onChange={(e) => handleNomeChange(e.target.value)}
          />
        ),
      },
      {
        accessorKey: "extracao.LoginCliente",
        header: "Cliente",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por cliente"
            variant="outlined"
            size="small"
            onChange={(e) => handleClienteChange(e.target.value)}
          />
        ),
      },
      {
        accessorKey: "extracao.LoginUsuario",
        header: "Login Usuário",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por usuário"
            variant="outlined"
            size="small"
            onChange={(e) => handleUsuarioChange(e.target.value)}
          />
        ),
      },
      {
        accessorKey: "extracao.NomeUsuario",
        header: "Nome Usuário",
        size: 150,
        enableColumnActions: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Filter: ({ column }) => (
          <TextField
            fullWidth
            placeholder="Buscar por nome de usuário"
            variant="outlined"
            size="small"
            onChange={(e) => handleNomeUsuarioChange(e.target.value)}
          />
        ),
      },
      {
        accessorKey: "extracao.Inclusao",
        header: "Data de Criação",
        size: 150,
        enableColumnActions: false,
        accessorFn: (rowData: any) => {
          return new Date(rowData.extracao.Inclusao).getTime() + 3 * 60 * 60 * 1000;
        },
        Cell: ({ cell }) => (
          <span>{format(cell.getValue<Date>(), "dd/MM/yyyy HH:mm:ss")}</span>
        ),
        Filter: () => (
          <Grid style={{ display: "flex", gap: "8px" }}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                label="Data Inicial"
                value={
                  extracaoListFiltros.inclusaoStart
                    ? new Date(extracaoListFiltros.inclusaoStart)
                    : null
                }
                onChange={debounce((newValue: Date | null, context) => {
                  if (context.validationError !== "invalidDate") {
                    if (newValue) {
                      setExtracaoListFiltrosInclusaoStart(newValue);
                    } else {
                      resetExtracaoListFiltrosInclusaoStart();
                    }
                  }
                }, Number(process.env.REACT_APP_TIMEOUT_DEBOUNCE))}
                disableFuture
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <DatePicker
                label="Data Final"
                value={
                  extracaoListFiltros.inclusaoEnd
                    ? new Date(extracaoListFiltros.inclusaoEnd)
                    : null
                }
                onChange={debounce((newValue: Date | null, context) => {
                  if (context.validationError !== "invalidDate") {
                    if (newValue) {
                      setExtracaoListFiltrosInclusaoEnd(newValue);
                    } else {
                      resetExtracaoListFiltrosInclusaoEnd();
                    }
                  }
                }, 1000)}
                disableFuture
              />
            </LocalizationProvider>
          </Grid>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        accessorKey: "extracao.Quantidade",
        header: "Quantidade",
        size: 150,
        enableColumnActions: false,
        enableColumnFilter: false,
        accessorFn: (rowData: any) => {
          return rowData.extracao.Quantidade;
        },
        Cell: ({ cell }) => (
          <span>{cell.getValue<number>().toLocaleString("pt-Br")}</span>
        ),
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
      {
        header: "Download",
        size: 100,
        enableColumnActions: false,
        Cell: ({ row }) => {
          const TipoPerfil: number = currentUser ? currentUser.IdVelesPerfilInterno : 0;
          const isOwner = currentUser && row.original.extracao.LoginUsuario === currentUser.LoginUsuario;

          if (
            (TipoPerfil === ITipoUsuario.USUARIO_VELES_COMERCIAL ||
              TipoPerfil === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE ||
              TipoPerfil === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL
            ) &&
            !isOwner
          ) {
            return (
              <Button disabled>
                <LockIcon color="primary" />
              </Button>
            );
          }

          return (
            <DownloadCell
              extracao={row.original}
              key={row.original.extracao.IdVelesGeoExtracao}
            />
          );
        },
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
    ];



    return columnsToSet;
  }, [
    handleNomeChange,
    handleClienteChange,
    handleNomeUsuarioChange,
    handleUsuarioChange,
    extracaoListFiltros.inclusaoStart,
    extracaoListFiltros.inclusaoEnd,
    setExtracaoListFiltrosInclusaoStart,
    resetExtracaoListFiltrosInclusaoStart,
    setExtracaoListFiltrosInclusaoEnd,
    resetExtracaoListFiltrosInclusaoEnd,
    currentUser?.IdVelesPerfilInterno,
    currentUser?.LoginUsuario,
  ]);

  const table = useMaterialReactTable({
    columns,
    data: extracoes,
    columnFilterDisplayMode: "popover",
    manualFiltering: true,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnFilters: true,
    enableSorting: false,
    enableExpandAll: false,
    enableDensityToggle: false,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: loading,
    },
    rowCount: extracoesTotal,
    manualPagination: true,

    displayColumnDefOptions: {
      "mrt-row-expand": {
        header: "Detalhes",
      },
    },
    localization: {
      ...MRT_Localization_PT_BR,
      expand: "Detalhes",
    },

    enableExpanding: true,

    renderDetailPanel: ({ row }) => (
      <Box
        sx={{
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: "16px",
          paddingBottom: "16px",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Detalhes da Extração
        </Typography>

        {/* Criado por */}
        <Typography variant="body2">
          <strong>Criado por:</strong> {row.original.extracao.LoginUsuario}
        </Typography>

        {/* Data Criação */}
        <Typography variant="body2">
          <strong>Data da Extração:</strong>{" "}
          {format(
            new Date(
              new Date(row.original.extracao.Inclusao).getTime() +
              3 * 60 * 60 * 1000
            ),
            "dd/MM/yyyy    HH:mm:ss"
          )}
        </Typography>
        <br />

        {/* Projeto */}
        <Typography variant="body2">
          <strong>Projeto:</strong>{" "}
          {(() => {
            const projeto = projetos.find(
              (projeto) =>
                projeto.IdVelesGeoProjeto ===
                row.original.extracao.IdVelesGeoProjeto
            );
            return projeto?.Nome ? projeto.Nome : "Nenhum Projeto Associado";
          })()}
        </Typography>

        {/* Criado por */}
        <Typography variant="body2">
          <strong>Criado por:</strong> {row.original.extracao.NomeUsuario}
        </Typography>

        {/* Data Criação */}
        <Typography variant="body2">
          <strong>Data da criação do projeto:</strong>{" "}
          {format(
            new Date(
              new Date(row.original.extracao.Inclusao).getTime() +
              3 * 60 * 60 * 1000
            ),
            "dd/MM/yyyy    HH:mm:ss"
          )}
        </Typography>

        <br />

        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textTransform="uppercase"
        >
          Filtros da Extração
        </Typography>

        {/* Filtros */}

        <Grid
          container
          spacing={0}
          sx={{ width: "100%", padding: " 0 16px", marginBottom: "-10px" }}
        >
          {/* Seção de Filtros */}
          <Grid item xs={12} md={12}>
            {row.original.extracao.Filtros &&
              Object.keys(row.original.extracao.Filtros).length > 0 && (
                <>
                  <br />
                  <Grid container spacing={2} sx={{ marginBottom: 0 }}>
                    {[
                      "cbo" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.cbo) &&
                        row.original.extracao.Filtros.cbo.length > 0 ? (
                        <RecorteCBOFilterPreview
                          cboFilter={row.original.extracao.Filtros.cbo}
                          key="cbo"
                        />
                      ) : null,

                      "listasCbo" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.listasCbo) &&
                        row.original.extracao.Filtros.listasCbo.length > 0 ? (
                        <ListasCboFilterPreview
                          listasCboFilterPreview={row.original.extracao.Filtros.listasCbo}
                          key="listasCbo"
                        />
                      ) : null,

                      "classeSocial" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.classeSocial) &&
                        row.original.extracao.Filtros.classeSocial.length > 0 ? (
                        <RecorteClasseSocialFilterPreview
                          classeSocialFilter={row.original.extracao.Filtros.classeSocial}
                          key="classeSocial"
                        />
                      ) : null,

                      "contato" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.contato) &&
                        row.original.extracao.Filtros.contato.length > 0 ? (
                        <RecorteContatoFilterPreview
                          contatoFilter={row.original.extracao.Filtros.contato}
                          key="contato"
                        />
                      ) : null,

                      "dataNascimento" in row.original.extracao.Filtros &&
                        row.original.extracao.Filtros.dataNascimento ? (
                        <RecorteDataNascimentoFilterPreview
                          dataNascimentoFilter={row.original.extracao.Filtros.dataNascimento}
                          key="dataNascimento"
                        />
                      ) : null,

                      "escolaridade" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.escolaridade) &&
                        row.original.extracao.Filtros.escolaridade.length > 0 ? (
                        <RecorteEscolaridadeFilterPreview
                          escolaridadeFilter={row.original.extracao.Filtros.escolaridade}
                          key="escolaridade"
                        />
                      ) : null,

                      "estadoCivil" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.estadoCivil) &&
                        row.original.extracao.Filtros.estadoCivil.length > 0 ? (
                        <RecorteEstadoCivilFilterPreview
                          estadoCivilFilter={row.original.extracao.Filtros.estadoCivil}
                          key="estadoCivil"
                        />
                      ) : null,

                      "idade" in row.original.extracao.Filtros &&
                        row.original.extracao.Filtros.idade ? (
                        <RecorteIdadeFilterPreview
                          idadeFilter={row.original.extracao.Filtros.idade}
                          key="idade"
                        />
                      ) : null,

                      "profissao" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.profissao) &&
                        row.original.extracao.Filtros.profissao.length > 0 ? (
                        <RecorteProfissaoFilterPreview
                          profissaoFilter={row.original.extracao.Filtros.profissao}
                          key="profissao"
                        />
                      ) : null,

                      "rendaPresumidaAnual" in row.original.extracao.Filtros &&
                        row.original.extracao.Filtros.rendaPresumidaAnual ? (
                        <RecorteRendaAnualFilterPreview
                          rendaAnualFilter={row.original.extracao.Filtros.rendaPresumidaAnual}
                          key="rendaPresumidaAnual"
                        />
                      ) : null,

                      "rendaPresumidaMensal" in row.original.extracao.Filtros &&
                        row.original.extracao.Filtros.rendaPresumidaMensal ? (
                        <RecorteRendaMensalFilterPreview
                          rendaMensalFilter={row.original.extracao.Filtros.rendaPresumidaMensal}
                          key="rendaPresumidaMensal"
                        />
                      ) : null,

                      "scoreVeles" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.scoreVeles) &&
                        row.original.extracao.Filtros.scoreVeles.length > 0 ? (
                        <RecorteScoreFilterPreview
                          scoreVelesFilter={row.original.extracao.Filtros.scoreVeles}
                          key="scoreVeles"
                        />
                      ) : null,

                      "sexo" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.sexo) &&
                        row.original.extracao.Filtros.sexo.length > 0 ? (
                        <RecorteSexoFilterPreview
                          sexoFilter={row.original.extracao.Filtros.sexo}
                          key="sexo"
                        />
                      ) : null,


                      "listasCidades" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.listasCidades) &&
                        (

                          true
                        ) &&
                        row.original.extracao.Filtros.listasCidades.length > 0 ? (
                        <ListasCidadesFilterPreview
                          listasCidadesFilterPreview={row.original.extracao.Filtros.listasCidades}
                          key="listasCidades"
                        />
                      ) : null,

                      "localizacao" in row.original.extracao.Filtros &&
                        Array.isArray(row.original.extracao.Filtros.localizacao) &&
                        row.original.extracao.Filtros.localizacao.length > 0 ? (
                        <RecorteLocalizacaoFilterPreview
                          localizacaoFilter={row.original.extracao.Filtros.localizacao}
                          key="localizacao"
                        />
                      ) : null,

                      < Grid
                        item
                        xs={12}
                        sx={{ width: "100%", marginLeft: "-16px" }}
                        key="listasCep"
                      >
                        {"listasCep" in row.original &&
                          Array.isArray(row.original.listasCep) &&
                          row.original.listasCep.length > 0 && (
                            <Grid container spacing={2}>
                              <Typography
                                variant="body1"
                                color="primary"
                                fontWeight="bold"
                                textTransform="uppercase"
                                sx={{ width: "100%", marginLeft: "16px" }}
                                mr={2}
                              >
                                Listas CEP
                              </Typography>
                              <br />
                              {row.original.listasCep.map((lista, idx) => {
                                const termoBusca =
                                  cepSearchTerms[lista.IdVelesGeoListaCEP ?? idx]?.replace(/\D/g, "") || "";

                                const cepsFiltrados = (lista.CEPs ?? [])
                                  .filter(
                                    (cep: string) =>
                                      !termoBusca ||
                                      cep.replace(/\D/g, "").includes(termoBusca)
                                  )
                                  .map((cep: string) => formatCep(cep));

                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={lista.IdVelesGeoListaCEP ?? idx}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Typography fontWeight="bold" variant="subtitle2">
                                        {lista.Nome}
                                      </Typography>
                                      <TextField
                                        size="small"
                                        placeholder="Buscar CEP"
                                        variant="outlined"
                                        onChange={(e) => {
                                          const raw = e.target.value.replace(/\D/g, "");
                                          const masked = formatCepProgressivo(raw);
                                          setCepSearchTerms((prev) => ({
                                            ...prev,
                                            [lista.IdVelesGeoListaCEP ?? idx]: masked,
                                          }));
                                        }}
                                        value={
                                          cepSearchTerms[lista.IdVelesGeoListaCEP ?? idx] || ""
                                        }
                                        sx={{ ml: 2, minWidth: 180 }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </Box>
                                    <AutoSizer disableHeight>
                                      {({ width }: { width: number }) => (
                                        <FixedSizeList
                                          height={cepsFiltrados.length <= 3 ? 175 : 250}
                                          style={{
                                            maxHeight: 150,
                                            background: "#fff",
                                          }}
                                          width={width}
                                          itemSize={46}
                                          itemCount={cepsFiltrados.length}
                                          overscanCount={15}
                                        >
                                          {({ index, style }) => (
                                            <ListItem style={style} key={index}>
                                              <ListItemText primary={cepsFiltrados[index]} />
                                            </ListItem>
                                          )}
                                        </FixedSizeList>
                                      )}
                                    </AutoSizer>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                      </Grid>,

                      <Grid
                        item
                        xs={12}
                        sx={{
                          width: "100%",
                          marginLeft: "-16px",
                          marginBottom: "16px",
                        }}
                        key="listasDeduplicacao"
                      >
                        {"listasDeduplicacao" in row.original &&
                          Array.isArray(row.original.listasDeduplicacao) &&
                          row.original.listasDeduplicacao.length > 0 && (
                            <Grid container spacing={2}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                sx={{ marginLeft: "16px", marginBottom: 1 }}
                              >
                                <Typography
                                  variant="body1"
                                  color="primary"
                                  fontWeight="bold"
                                  textTransform="uppercase"
                                >
                                  Listas Deduplicação
                                </Typography>
                                <Tooltip title="Compara os CPFs da extração com os da(s) lista(s) de deduplicação">
                                  <Button
                                    sx={{ width: "240px" }}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      setVefificarCpfsDuplicados(
                                        row.original.extracao.IdVelesGeoExtracao,
                                        row.original.listasDeduplicacao!
                                      );
                                    }}
                                  >
                                    Verificar CPFs duplicados
                                  </Button>
                                </Tooltip>
                              </Box>

                              <br />
                              {row.original.listasDeduplicacao.map((lista, idx) => {
                                const termoBusca =
                                  cpfSearchTerms[lista.IdVelesGeoListaDeduplicacao ?? idx]?.replace(/\D/g, "") || "";

                                const cpfsFiltrados = (lista.CPFs ?? []).filter((cpf: string) => {
                                  return (
                                    !termoBusca || cpf.replace(/\D/g, "").includes(termoBusca)
                                  );
                                });

                                return (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={lista.IdVelesGeoListaDeduplicacao ?? idx}
                                  >
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Typography fontWeight="bold" variant="subtitle2">
                                        {lista.Nome}
                                      </Typography>
                                      <TextField
                                        size="small"
                                        placeholder="Buscar CPF"
                                        variant="outlined"
                                        onChange={(e) => {
                                          const raw = e.target.value.replace(/\D/g, "");
                                          const masked = formatCPFProgressivo(raw);
                                          setCpfSearchTerms((prev) => ({
                                            ...prev,
                                            [lista.IdVelesGeoListaDeduplicacao ?? idx]: masked,
                                          }));
                                        }}
                                        value={
                                          cpfSearchTerms[lista.IdVelesGeoListaDeduplicacao ?? idx] || ""
                                        }
                                        sx={{ ml: 2, minWidth: 180 }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </Box>
                                    <AutoSizer disableHeight>
                                      {({ width }: { width: number }) => (
                                        <FixedSizeList
                                          height={cpfsFiltrados.length <= 3 ? 175 : 250}
                                          style={{
                                            maxHeight: 150,
                                            background: "#fff",
                                          }}
                                          width={width}
                                          itemSize={46}
                                          itemCount={cpfsFiltrados.length}
                                          overscanCount={15}
                                        >
                                          {({ index, style }) => {
                                            const cpf = cpfsFiltrados[index];
                                            const cpfNormalizado = cpf.replace(/\D/g, "");

                                            const isDuplicado =
                                              lista.CPFsDuplicados?.includes(cpfNormalizado) ?? false;

                                            return (
                                              <ListItem
                                                style={{
                                                  ...style,
                                                  backgroundColor: isDuplicado ? "#fff3cd" : "#fff",
                                                }}
                                                key={index}
                                              >
                                                <ListItemText
                                                  primary={formatCPF(cpf)}
                                                  primaryTypographyProps={{
                                                    fontWeight: isDuplicado ? "bold" : "normal",
                                                    color: isDuplicado ? "primary" : "inherit",
                                                  }}
                                                />
                                              </ListItem>
                                            );
                                          }}
                                        </FixedSizeList>
                                      )}
                                    </AutoSizer>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                      </Grid>,
                    ].filter(Boolean)}
                  </Grid>
                </>
              )}
          </Grid>
        </Grid>

        {/* Recortes */}
        < Grid
          item
          xs={12}
          md={12}
          sx={{ width: "100%", marginLeft: "-16px", marginBottom: "16px" }}
        >
          {(() => {
            const recorteExtracao = recortesExtracao.find(
              (recorte) =>
                recorte.IdVelesGeoProjeto ===
                row.original.extracao.IdVelesGeoProjeto &&
                areRecortesIguais(
                  recorte.Recortes,
                  row.original.extracao.Recortes
                )
            );

            if (
              recorteExtracao &&
              Array.isArray(recorteExtracao.Recortes) &&
              recorteExtracao.Recortes.length > 0
            ) {
              return (
                <Typography
                  variant="body1"
                  color="primary"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  <br />
                  Recortes Extração
                  <br />
                  <RecortesProjetoList
                    temRecorteExtracao={recorteExtracao}
                    modoSomenteLeitura={true}
                  />
                </Typography>
              );
            }

            return (
              <Typography
                variant="body2"
                color="black"
                fontWeight="bold"
                textTransform="uppercase"
                textAlign="left"
              >
                Sem recortes para esta extração
              </Typography>
            );
          })()}
        </Grid >

        {/* CPFs extraídos */}
        < Grid
          item
          xs={12}
          md={12}
          sx={{ width: "100%", marginLeft: "-16px" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            sx={{ marginLeft: "0px", marginBottom: 1 }}
          >
            <Typography
              variant="body1"
              color="primary"
              fontWeight="bold"
              textTransform="uppercase"
            >
              <br />
              CPFs Extraídos
            </Typography>
            <TextField
              size="small"
              placeholder="Buscar CPF"
              variant="outlined"
              onChange={(e) => {
                const raw = e.target.value.replace(/\D/g, "");
                const masked = formatCPFProgressivo(raw);
                setCpfSearchTerms((prev) => ({
                  ...prev,
                  [row.original.extracao.IdVelesGeoExtracao ?? ""]: masked,
                }));
              }}
              value={
                cpfSearchTerms[
                row.original.extracao.IdVelesGeoExtracao ?? ""
                ] || ""
              }
              sx={{ ml: 2, minWidth: 180, marginLeft: "16px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <AutoSizer disableHeight>
            {({ width }: { width: number }) => {
              const searchTerm =
                cpfSearchTerms[
                row.original.extracao.IdVelesGeoExtracao ?? ""
                ] || "";
              const cpfsOriginais = row.original.extracao.CPFs || [];

              const cpfsFiltrados = searchTerm
                ? cpfsOriginais.filter((cpf) =>
                  cpf
                    .replace(/\D/g, "")
                    .includes(searchTerm.replace(/\D/g, ""))
                )
                : cpfsOriginais;

              return (
                <FixedSizeList
                  height={cpfsFiltrados.length <= 3 ? 175 : 250}
                  style={{
                    maxHeight: 400,
                    background: "#fff",
                  }}
                  width={width}
                  itemSize={46}
                  itemCount={cpfsFiltrados.length}
                  overscanCount={15}
                >
                  {({ index, style }) => {
                    const cpf = cpfsFiltrados[index];
                    return (
                      <ListItem
                        style={{
                          ...style,
                        }}
                        key={index}
                      >
                        <ListItemText primary={formatCPF(cpf)} />
                      </ListItem>
                    );
                  }}
                </FixedSizeList>
              );
            }}
          </AutoSizer>
        </Grid >
      </Box >
    ),
  });

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    setExtracoesPagination({
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
    });
  }, [pagination, setExtracoesPagination]);

  useEffect(() => {
    if (!isFirstRender.current) {
      setLoading(false);
      isFirstRender.current = true;
      setActivePage({ pageKey: "Geo-Veles/Extracoes" });
    }
  }, [setActivePage]);

  useEffect(() => {
    if (!loading && extracoes.length === 0) {
      getExtracoes();
    }
  }, [extracaoListFiltros, getExtracoes, loading]);

  useEffect(() => {
    getExtracoes();
  }, [extracoesPagination, getExtracoes]);

  useEffect(() => {
    if (!loading) {
      getExtracoesFindProjetos();
    }
  }, [getExtracoes, getExtracoesFindProjetos, loading]);

  useEffect(() => {
    if (!loading) {
      getRecortes();
    }
  }, [getExtracoes, getRecortes]);

  useEffect(() => {
    let listasDeduplicacaoExtracao: IListaDeduplicacao[] = [];
    for (let i: number = 0; i < extracoes.length; i++) {
      if (extracoes[i].listasDeduplicacao) {
        if (extracoes[i].listasDeduplicacao!.length > 0) {
          listasDeduplicacaoExtracao.push(...extracoes[i]!.listasDeduplicacao!);
        }
      }
    }

    getListasDeduplicacaoExtracao({
      listasDeduplicacaoExtracao: listasDeduplicacaoExtracao,
    });
  }, []);

  useEffect(() => {
    changeTitleAndMetaDesc({
      metaDesc: "Lista de extrações",
      title: "Veles_Geo - Lista de Extrações",
    });
  }, [changeTitleAndMetaDesc]);

  useEffect(() => {
    return () => {
      resetExtracaoListFiltros();
      setExtracoesPagination({ page: 1, limit: 5 });
    };
  }, [resetExtracaoListFiltros, setExtracoesPagination]);

  return loading ? (
    <SkeletonVelesTable />
  ) : (
    <>
      <Typography color="primary" fontWeight="bold" variant="h6" gutterBottom>
        Lista de Extrações
      </Typography>
      <Divider
        style={{
          margin: "16px 0",
          borderBottomWidth: "2px",
          borderBottomColor: "rgba(0,0,0,0.5)",
        }}
      />
      <Grid container justifyContent={"space-between"}>
        <Grid item>
          <Button
            hidden
            style={{
              marginBottom: "10px",
            }}
            variant="contained"
            color="primary"
            onClick={() => { }}
          >
            <Add
              style={{
                marginRight: "8px",
              }}
            />{" "}
            --------
          </Button>
        </Grid>
        <Grid item>
          <Button
            disabled={
              extracaoListFiltros.nome === undefined &&
              extracaoListFiltros.cliente === undefined &&
              extracaoListFiltros.usuario === undefined &&
              extracaoListFiltros.nomeUsuario === undefined &&
              extracaoListFiltros.inclusaoStart === undefined &&
              extracaoListFiltros.inclusaoEnd === undefined
            }
            style={{
              marginBottom: "10px",
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              resetExtracaoListFiltros();
            }}
          >
            <FilterAltOff
              style={{
                marginRight: "8px",
              }}
            />{" "}
            Limpar Filtros
          </Button>
        </Grid>
      </Grid>
      <MaterialReactTable table={table} />
    </>
  );
};